import React from 'react'
import { Grid, Box, Typography, Card, CardContent, Skeleton } from '@mui/material'
import { PeopleOutlined as PeopleIcon, CalendarMonthOutlined as CalendarIcon, CampaignOutlined as CampaignIcon, AutoAwesomeOutlined as SmartToyIcon } from '@mui/icons-material'
import { SMBDashboardSummary } from '../../../services/api'

interface MetricCardProps {
  label: string
  value: number | null
  icon: React.ReactNode
  loading: boolean
  color: string
  highlight?: boolean
}

const MetricCard: React.FC<MetricCardProps> = ({ label, value, icon, loading, color, highlight }) => (
  <Card 
    sx={{ 
      height: '100%',
      boxShadow: highlight ? '0 10px 15px -3px rgba(99, 102, 241, 0.15)' : '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -2px rgba(0,0,0,0.025)',
      border: '1px solid',
      borderColor: highlight ? 'rgba(99, 102, 241, 0.3)' : '#F1F5F9',
      borderRadius: '16px',
      background: highlight ? 'linear-gradient(180deg, #ffffff 0%, #FAFAFF 100%)' : '#fff',
      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: highlight ? '0 20px 25px -5px rgba(99, 102, 241, 0.15)' : '0 10px 15px -3px rgba(0,0,0,0.08)',
      }
    }}
  >
    <CardContent sx={{ p: { xs: '16px !important', sm: '24px !important' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 1.5, sm: 3 } }}>
        <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>{label}</Typography>
        <Box sx={{ color, bgcolor: `${color}15`, display: 'flex', p: { xs: 0.5, sm: 1 }, borderRadius: '12px' }}>{icon}</Box>
      </Box>
      {loading ? (
        <Skeleton variant="text" width="60%" height={60} />
      ) : (
        <Typography variant="h3" sx={{ 
          fontWeight: 800, 
          color: highlight ? '#4F46E5' : '#0F172A', 
          letterSpacing: '-1px', 
          '@media (max-width:599px)': { fontSize: '1.75rem' } 
        }}>
          {value?.toLocaleString() ?? '0'}
        </Typography>
      )}
    </CardContent>
  </Card>
)

interface MetricCardsRowProps {
  summary: SMBDashboardSummary | null;
  summaryLoading: boolean;
}

export const MetricCardsRow: React.FC<MetricCardsRowProps> = ({ summary, summaryLoading }) => {
  return (
    <>
      <Box sx={{ display: 'flex', gap:2, alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight={700} color="text.primary">
          Performance Overview
        </Typography>
        <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ bgcolor: 'rgba(0,0,0,0.04)', px: 1.5, py: 0.5, borderRadius: 2 }}>
          Last 30 Days
        </Typography>
      </Box>
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 3 }}>
        {[
          { label: 'Total Leads', value: summary?.total_leads ?? null, icon: <PeopleIcon />, color: '#6366f1' },
          { label: 'Total Bookings', value: summary?.total_bookings ?? null, icon: <CalendarIcon />, color: '#10b981' },
          { label: 'Total Campaigns', value: summary?.total_campaigns ?? null, icon: <CampaignIcon />, color: '#f59e0b' },
          { label: 'AI Messages Sent', value: summary?.ai_messages_sent ?? null, icon: <SmartToyIcon />, color: '#3b82f6' },
        ].map(({ label, value, icon, color }) => (
          <Grid item xs={6} sm={6} lg={3} key={label}>
            <MetricCard label={label} value={value} icon={icon} loading={summaryLoading && !summary} color={color} />
          </Grid>
        ))}
      </Grid>
    </>
  )
}
